import React, { useState } from "react";
import {
  useGetAdminNotificationsQuery,
  useUpdateAdminNotificationMutation,
} from "../../../../store/slices/api";
import { failedMessage } from "../../../shared/serverReason";

const FILTERS = [
  { key: "", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
  { key: "resolved", label: "Resolved" },
];

const CATEGORY_LABELS = {
  more_questions: "More questions",
  problem: "Problem report",
  // From "Have us check my login" on the signed-out welcome card. That person
  // wasn't signed in, so the name and email in it are only what they typed.
  login_help: "Login help request",
  other: "Message",
};

const MESSAGE_HEADINGS = {
  more_questions: "Reason for request",
  problem: "What they reported",
  login_help: "From the signed-out welcome card (not signed in)",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function NotificationCard({ n, onUpdate, isUpdating }) {
  const unread = n.status === "unread";
  return (
    <div
      className={`rounded-[16px] shadow-md p-[20px] border transition-colors
        ${
          unread
            ? "bg-white dark:bg-darkCard border-dupontGold dark:border-darkGold"
            : "bg-dupontCream/50 dark:bg-darkCard/60 border-dupontGold/20 dark:border-darkGold/20"
        }`}
    >
      <div className="flex items-start justify-between gap-x-[12px]">
        <div className="flex flex-col">
          <div className="flex items-center gap-x-[8px]">
            {unread && (
              <span className="w-[8px] h-[8px] rounded-full bg-dupontGold dark:bg-darkGold" />
            )}
            <span className="text-[15px] font-bold text-dupontBlue dark:text-darkText">
              {n.submitter_name ||
                (n.category === "login_help" ? "Someone signed out" : "Family member")}
            </span>
            <span className="text-[12px] font-semibold px-[8px] py-[2px] rounded-full bg-dupontGold/15 dark:bg-darkGold/15 text-dupontGold dark:text-darkGold">
              {CATEGORY_LABELS[n.category] || CATEGORY_LABELS.other}
            </span>
          </div>
          <span className="text-[13px] text-dupontGray dark:text-darkTextSecondary mt-[2px]">
            {formatDate(n.created_at)}
            {n.status === "resolved" && " · resolved"}
          </span>
        </div>
        <div className="flex gap-x-[8px] shrink-0">
          {unread && (
            <button
              onClick={() => onUpdate({ id: n.id, status: "read" })}
              disabled={isUpdating}
              className="text-[13px] font-semibold px-[12px] py-[6px] rounded-[8px]
                border border-dupontGold/40 dark:border-darkGold/40
                text-dupontBlue dark:text-darkText
                hover:bg-dupontGold/10 dark:hover:bg-darkGold/10
                disabled:opacity-50 transition-colors cursor-pointer"
            >
              Mark read
            </button>
          )}
          {n.status !== "resolved" && (
            <button
              onClick={() => onUpdate({ id: n.id, status: "resolved" })}
              disabled={isUpdating}
              className="text-[13px] font-semibold px-[12px] py-[6px] rounded-[8px]
                bg-dupontGold dark:bg-darkGold text-white
                hover:bg-dupontGold/90 dark:hover:bg-darkGold/90
                disabled:opacity-50 transition-colors cursor-pointer"
            >
              Resolve
            </button>
          )}
          {n.status === "resolved" && (
            <button
              onClick={() => onUpdate({ id: n.id, status: "unread" })}
              disabled={isUpdating}
              className="text-[13px] font-semibold px-[12px] py-[6px] rounded-[8px]
                border border-dupontGold/40 dark:border-darkGold/40
                text-dupontBlue dark:text-darkText
                hover:bg-dupontGold/10 dark:hover:bg-darkGold/10
                disabled:opacity-50 transition-colors cursor-pointer"
            >
              Reopen
            </button>
          )}
        </div>
      </div>
      {/* The reason / message — clearly labeled so admins immediately see why
          they were contacted. */}
      <div className="mt-[14px] pt-[12px] border-t border-dupontGold/15 dark:border-darkGold/15">
        <p className="text-[12px] font-display font-bold uppercase tracking-wide text-dupontGold dark:text-darkGold mb-[6px]">
          {MESSAGE_HEADINGS[n.category] || "Message"}
        </p>
        <p className="text-[16px] font-body text-dupontBlue dark:text-darkText whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed">
          {n.message}
        </p>
      </div>

      {/* FUTURE (flagged, not built yet): a "Grant N more questions this week"
          button for admins lives here. Cleanest implementation: a small
          per-user weekly bonus (e.g. a WeeklyQuestionGrant row or a
          bonus_questions column keyed to the week), and get_quota() adds the
          bonus to WEEKLY_LIMIT. See docs/META-PLAN §"Future: grant more
          questions" in the backend repo. Deliberately deferred 2026-06-02. */}
    </div>
  );
}

function Notifications() {
  const [filter, setFilter] = useState("");
  const { data, isFetching, isError, refetch } =
    useGetAdminNotificationsQuery(filter || undefined);
  const [updateNotification, { isLoading: isUpdating }] =
    useUpdateAdminNotificationMutation();
  const [updateError, setUpdateError] = useState(null);

  const handleUpdate = async (change) => {
    setUpdateError(null);
    try {
      await updateNotification(change).unwrap();
    } catch (err) {
      setUpdateError(failedMessage(err, "That change didn't save. Please try again."));
    }
  };

  const notifications = data?.notifications || [];
  const unreadCount = data?.unread_count || 0;

  return (
    <div className="flex flex-col gap-y-[24px]">
      {/* Filter bar */}
      <div className="flex items-center justify-between flex-wrap gap-y-[12px]">
        <div className="flex gap-x-[8px]">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setFilter(f.key)}
              className={`text-[14px] font-semibold px-[14px] py-[8px] rounded-[10px] transition-colors cursor-pointer
                ${
                  filter === f.key
                    ? "bg-dupontGold dark:bg-darkGold text-white"
                    : "bg-white dark:bg-darkCard text-dupontBlue dark:text-darkText border border-dupontGold/30 dark:border-darkGold/30 hover:bg-dupontGold/10 dark:hover:bg-darkGold/10"
                }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-[14px] text-dupontGray dark:text-darkTextSecondary">
          {unreadCount} unread
        </span>
      </div>

      {updateError && (
        <p role="alert" className="text-[14px] font-body text-red-700 dark:text-red-400">
          {updateError}
        </p>
      )}

      {/* List */}
      {isFetching ? (
        <p className="text-[15px] text-dupontGray dark:text-darkTextSecondary text-center py-[40px]">
          Loading…
        </p>
      ) : isError ? (
        // Not "No notifications": a failed load is not an empty inbox.
        <div className="bg-white dark:bg-darkCard rounded-[16px] shadow-md p-[40px] border border-dupontGold/30 dark:border-darkGold/20 text-center">
          <p className="text-[16px] font-body text-red-700 dark:text-red-400">
            The notifications didn't load.
          </p>
          <button
            type="button"
            onClick={refetch}
            className="mt-[12px] text-[14px] font-semibold px-[14px] py-[8px] rounded-[10px]
              bg-dupontGold dark:bg-darkGold text-white
              hover:bg-dupontGold/90 dark:hover:bg-darkGold/90 transition-colors cursor-pointer"
          >
            Try again
          </button>
        </div>
      ) : notifications.length === 0 ? (
        <div className="bg-white dark:bg-darkCard rounded-[16px] shadow-md p-[40px] border border-dupontGold/30 dark:border-darkGold/20 text-center">
          <p className="text-[16px] font-body text-dupontBlue/70 dark:text-darkText/70">
            No notifications here.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-y-[14px]">
          {notifications.map((n) => (
            <NotificationCard
              key={n.id}
              n={n}
              onUpdate={handleUpdate}
              isUpdating={isUpdating}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Notifications;
